import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { BiHighlight, BiMessageSquareDetail } from 'react-icons/bi';
import { IoTrashOutline } from 'react-icons/io5';
import {
    MdLightbulb,
    MdClose,
    MdOutlineCalculate,
    MdSuperscript,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight
} from 'react-icons/md';
import axios from 'config/axios';
import Loader from 'components/Loader';
import AnnotationHelper from 'components/AnnotationHelper';
import { useAppSelector } from 'hooks/useStore';
import { measurePassageSelectionOffsets, generatePassageHTML } from 'global/passageHighlight';
import Reference from 'pages/private/Digital/Module/Reference';
import 'katex/dist/katex.min.css';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './digitalModule.css';
import './dailyQuestionCustom.css';
import { flattenKatexUnderlines } from 'global/katexWrap';

// Utility function to convert Vimeo URL to embed URL
// Annotation color and underline constants
const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};

// Annotation type
type AnnotationType = {
    passageId: string;
    startIndex: number;
    endIndex: number;
    annotation: string;
    html?: string;
    preview?: string;
    color?: string;
    underline?: string;
};


function DailyQuestion() {
    const [loading, setLoading] = useState(true);
    const [question, setQuestion] = useState<any>(null);
    const [answer, setAnswer] = useState('');
    const [crossedOut, setCrossedOut] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [timeSpent, setTimeSpent] = useState(0);
    const [explanationOpen, setExplanationOpen] = useState(false);
    
    // Toolbar states
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [showCrossOutButtons, setShowCrossOutButtons] = useState(false);

    // Annotation states
    const [annotate, setAnnotate] = useState(false);
    const [annotations, setAnnotations] = useState<AnnotationType[]>([]);
    const [clickedAnnotation, setClickedAnnotation] = useState<AnnotationType | null>(null);
    const [annotation, setAnnotation] = useState<AnnotationType | null>(null);
    const [annotationColor, setAnnotationColor] = useState(Object.keys(COLORS)[0]);
    const [annotationUnderline, setAnnotationUnderline] = useState<string | null>('Solid');
    const [notesOpen, setNotesOpen] = useState(false);

    const { user } = useAppSelector(state => state.user);

    useEffect(() => {
        loadQuestion();
        
        // Timer for time tracking
        const timer = setInterval(() => {
            if (!submitted) {
                setTimeSpent(prev => prev + 1);
            }
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);

    // Render KaTeX math after question loads (matching student site implementation)
    const renderKatex = () => {
        const katexElements = document.getElementsByClassName('katex');
        for (let i = 0; i < katexElements.length; i++) {
            renderMathInElement(katexElements[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katexElements[i].innerHTML = katexElements[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        // Handle underlines (for R&W questions)
        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (question?.subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            } else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        // Remove mathml elements (accessibility duplicates that cause text duplication)
        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    };

    useEffect(() => {
        if (question) {
            renderKatex();
        }
    }, [question]);

    // Render KaTeX when result/explanation is shown
    useEffect(() => {
        if (result) {
            setTimeout(() => {
                renderKatex();
            }, 100);
        }
    }, [result]);

    // Add selection change listener for annotations
    useEffect(() => {
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, []);

    // Position annotation helper when annotation is selected
    useEffect(() => {
        const selectedAnnotation = document.getElementById('digitalModule__passageAnnotation--selected');

        if (selectedAnnotation) {
            const annotationMarks = selectedAnnotation.getElementsByTagName('mark');
            const passageContainer = document.getElementById('digitalModule__passages');
            if (annotationMarks.length > 0 && passageContainer) {
                let annotationMark = annotationMarks[0] as HTMLElement;
                const annotationWidth = annotationMark.offsetWidth;
                
                let leftOffset = 0;
                let topOffset = -passageContainer.scrollTop;
                while (passageContainer.contains(annotationMark)) {
                    leftOffset += annotationMark.offsetLeft;
                    topOffset += annotationMark.offsetTop;
                    annotationMark = annotationMark.parentElement as HTMLElement;
                }

                const annotationHelper = document.getElementById('digitalModule__passageAnnotationHelper');
                if (annotationHelper) {
                    let left = leftOffset - annotationHelper.offsetWidth/2 + annotationWidth/2;
                    let top = topOffset - 55;
                    left = Math.max(Math.min(left, passageContainer.offsetWidth - annotationHelper.offsetWidth), 0);
                    annotationHelper.style.left = left + 'px';
                    annotationHelper.style.top = top + 'px';
                }
            }
        }

        const selectedNotes = document.getElementById('digitalModule__notesCard--active');
        if (selectedNotes) {
            selectedNotes.scrollIntoView();
        }
    }, [annotation]);

    // Scroll notes into view when notes panel opens
    useEffect(() => {
        const selectedNotes = document.getElementById('digitalModule__notesCard--active');
        if (selectedNotes) {
            selectedNotes.scrollIntoView();
        }
    }, [notesOpen]);

    const loadQuestion = () => {
        axios.get('questions/daily/')
            .then(res => {
                setQuestion(res.data);
                
                // Check localStorage for anonymous users
                const localKey = `daily_${res.data.date}`;
                const localAnswer = localStorage.getItem(localKey);
                
                if (res.data.alreadyAnswered && res.data.previousAnswer) {
                    // Authenticated user already answered
                    setSubmitted(true);
                    setAnswer(res.data.previousAnswer.answer || '');
                    setCrossedOut(res.data.previousAnswer.crossedOut || []);
                    setResult({
                        isCorrect: res.data.previousAnswer.isCorrect || false,
                        explanation: res.data.explanation || '',
                        correctPercentage: res.data.stats?.correctPercentage || 0,
                        totalAttempts: res.data.stats?.totalAttempts || 0
                    });
                } else if (localAnswer && !res.data.isAuthenticated) {
                    // Anonymous user already answered (stored in localStorage)
                    const stored = JSON.parse(localAnswer);
                    setSubmitted(true);
                    setAnswer(stored.answer);
                    setCrossedOut(stored.crossedOut || []);
                    setResult(stored.result);
                }
                
                setLoading(false);
            })
            .catch(error => {
                console.error(error);
                setLoading(false);
            });
    };

    const handleAnswerSelect = (choiceId: string) => {
        if (submitted) return;
        setAnswer(answer === choiceId ? '' : choiceId);
    };

    const handleAnswerInput = (value: string) => {
        if (submitted) return;
        setAnswer(value);
    };

    const handleCrossOut = (choiceId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (submitted) return;
        
        if (crossedOut.includes(choiceId)) {
            setCrossedOut(crossedOut.filter(id => id !== choiceId));
        } else {
            setCrossedOut([...crossedOut, choiceId]);
        }
        
        // If user crosses out the selected answer, deselect it
        if (choiceId === answer) {
            setAnswer('');
        }
    };

    // Annotation functions
    const saveAnnotation = (newAnnotation: AnnotationType) => {
        const index = annotations.findIndex(existing => (
            existing.passageId === newAnnotation.passageId &&
            existing.startIndex === newAnnotation.startIndex &&
            existing.endIndex === newAnnotation.endIndex
        ));
        
        if (index >= 0) {
            const newAnnotations = [...annotations];
            newAnnotations[index] = newAnnotation;
            setAnnotations(newAnnotations);
        } else {
            // Check for covered annotations
            const coveredAnnotations = annotations.filter(existing => (
                existing.passageId === newAnnotation.passageId &&
                existing.startIndex >= newAnnotation.startIndex &&
                existing.endIndex <= newAnnotation.endIndex
            ));

            let newAnnotations = [...annotations, newAnnotation];

            for (let i = 0; i < coveredAnnotations.length; i++) {
                const coveredIndex = newAnnotations.findIndex(existing => (
                    existing.passageId === coveredAnnotations[i].passageId &&
                    existing.startIndex === coveredAnnotations[i].startIndex &&
                    existing.endIndex === coveredAnnotations[i].endIndex
                ));

                const coveredAnnotation = newAnnotations.splice(coveredIndex, 1)[0];
                newAnnotations.push(coveredAnnotation);
            }
            
            setAnnotations(newAnnotations);
        }
    };

    const deleteAnnotation = (annotationToDelete: AnnotationType) => {
        const index = annotations.findIndex(existing => (
            existing.passageId === annotationToDelete.passageId &&
            existing.startIndex === annotationToDelete.startIndex &&
            existing.endIndex === annotationToDelete.endIndex
        ));
        
        if (index >= 0) {
            const newAnnotations = [...annotations];
            newAnnotations.splice(index, 1);
            setAnnotations(newAnnotations);
        }
    };

    const removeNotes = (event: React.MouseEvent, ann: AnnotationType) => {
        event.stopPropagation();
        saveAnnotation({
            ...ann,
            annotation: null
        });
        setAnnotation({
            ...ann,
            annotation: null
        });
    };

    const handleSelectionChange = () => {
        const selection = window.getSelection();
        const passages = document.getElementsByClassName('digitalModule__passageText');

        if (!selection) {
            Array.from(passages).forEach(passage => {
                passage.classList.remove('digitalModule__passageSelection');
            });
            setAnnotation(null);
            return;
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        if (Array.from(passages).some(passage => passage.contains(anchorNode)) && (
                anchorNode !== focusNode || anchorOffset !== focusOffset
            )) {
            Array.from(passages).forEach(passage => {
                passage.classList.add('digitalModule__passageSelection');
            });
        } else {
            Array.from(passages).forEach(passage => {
                passage.classList.remove('digitalModule__passageSelection');
            });

            const notes = document.getElementById('digitalModule__notes');
            if (notes && (notes.contains(anchorNode) || notes.contains(focusNode))) {
                return;
            }

            // Don't clear annotation if clicking on the annotation helper
            const annotationHelper = document.getElementById('digitalModule__passageAnnotationHelper');
            if (annotationHelper && (annotationHelper.contains(anchorNode) || annotationHelper.contains(focusNode))) {
                return;
            }

            setAnnotation(null);
        }
    };

    const handleAnnotationMouseDown = (ann: AnnotationType) => {
        const annotationElements = document.getElementsByClassName('digitalModule__passageAnnotation');

        for (let i = 0; i < annotationElements.length; i++) {
            annotationElements[i].classList.add('digitalModule__passageAnnotation--ignore');
        }

        setClickedAnnotation(ann);
    };

    const handlePassageMouseUp = () => {
        const annotationElements = document.getElementsByClassName('digitalModule__passageAnnotation');

        for (let i = 0; i < annotationElements.length; i++) {
            annotationElements[i].classList.remove('digitalModule__passageAnnotation--ignore');
        }

        const selection = window.getSelection();
        
        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        if (
            anchorNode === focusNode &&
            anchorOffset === focusOffset
        ) {
            setAnnotation(clickedAnnotation);
        }

        setClickedAnnotation(null);

        const passages = document.getElementsByClassName('digitalModule__passageText');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));

        if (!passage) {
            return;
        }

        // Flat textContent offsets, snapped outward so a boundary never
        // splits a rendered KaTeX construct (e.g. an accented character).
        const offsets = measurePassageSelectionOffsets(passage, selection);

        if (!offsets) {
            return;
        }

        const { startIndex: startOffset, endIndex: endOffset } = offsets;
        const { passageHTML, passagePreview } = generatePassageHTML(passage, startOffset, endOffset);

        if (annotate) {
            Array.from(passages).forEach(p => {
                p.classList.remove('digitalModule__passageSelection');
            });
            const newAnn: AnnotationType = {
                passageId: passage.id,
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: null,
                html: passageHTML,
                preview: passagePreview,
                color: annotationColor,
                underline: annotationUnderline
            };
            saveAnnotation(newAnn);
            
            // Clear browser text selection in auto-highlight mode
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
        } else {
            setAnnotation({
                passageId: passage.id,
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: null,
                html: passageHTML,
                preview: passagePreview
            });
        }
    };

    const handleSubmit = () => {
        if (!answer) return;
        
        setLoading(true);
        
        axios.post('questions/daily/submit/', {
                dailyQuestionId: question.id,
                answer: answer,
                crossedOut: crossedOut,
                timeSpent: timeSpent
            })
            .then(res => {
                setResult(res.data);
                setSubmitted(true);
                
                // Save to localStorage for anonymous users
                if (!question.isAuthenticated) {
                    const localKey = `daily_${question.date}`;
                    localStorage.setItem(localKey, JSON.stringify({
                        answer: answer,
                        crossedOut: crossedOut,
                        result: res.data
                    }));
                }
                
                setLoading(false);
            })
            .catch(error => {
                // Handle "Already answered today" error first
                if (error.response?.status === 409 && error.response?.data?.error === "Already answered today") {
                    // User has already answered, reload the question data to show their previous answer
                    loadQuestion();
                    return;
                }
                
                // Log other errors
                console.error('Submit error:', error);
                console.error('Error response:', error.response?.data);
                console.error('Error status:', error.response?.status);
                
                // Handle other errors - could show a toast notification or alert
                alert('An error occurred while submitting your answer. Please try again.');
                
                setLoading(false);
            });
    };

    if (loading) return <Loader />;
    
    if (!question) {
        return (
            <div className='dailyQuestion__page dailyQuestion'>
                <div className='dailyQuestion__empty'>
                    <h1>📅 Daily Question</h1>
                    <p>No question available today. Check back tomorrow!</p>
                </div>
            </div>
        );
    }

    return (
        <div className='dailyQuestion__page dailyQuestion digitalModule'>
            <Helmet>
                <title>Daily Question - SAT Crash Course</title>
            </Helmet>

            {/* Module Header with Directions */}
            <div className='digitalModule__header dailyQuestion__header'>
                <div className='digitalModule__titleGroup'>
                    <h1 className='digitalModule__title'>{question.subject || 'Daily Question'}</h1>
                </div>
                {!submitted && (
                    <div className='digitalModule__actionGroup'>
                        {question.subject === 'Math' && <>
                            <button
                                className={referenceOpen ? 'digitalModule__action--active' : 'digitalModule__action'}
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='digitalModule__actionIcon' />
                                <p className='digitalModule__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen ? 'digitalModule__action--active' : 'digitalModule__action'}
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='digitalModule__actionIcon' />
                                <p className='digitalModule__actionText'>Calculator</p>
                            </button>
                        </>}
                        {question.subject === 'Reading and Writing' && (
                            <button 
                                className={annotate ? 'digitalModule__action--active' : 'digitalModule__action'}
                                onClick={() => setAnnotate(!annotate)}
                            >
                                <div className='digitalModule__actionIconGroup'>
                                    <BiHighlight className='digitalModule__actionIcon' />
                                    <BiMessageSquareDetail className='digitalModule__actionIcon' />
                                </div>
                                <p className='digitalModule__actionText'>Highlights & Notes</p>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Calculator Popup */}
            {question.subject === 'Math' && (
                <DesmosCalculatorPanel
                    open={calculatorOpen}
                    onClose={() => setCalculatorOpen(false)}
                    headerClassName='digitalModule__popupHeader'
                />
            )}

            {/* Reference Popup */}
            {question.subject === 'Math' && referenceOpen && (
                <div id='referenceDrag' className='digitalModule__popup--reference'>
                    <div id='referenceHeader' className='digitalModule__popupHeader'>
                        <h3 className='digitalModule__popupHeaderText'>Reference</h3>
                        <MdClose className='digitalModule__popupHeaderClose' onClick={() => setReferenceOpen(false)} />
                    </div>
                    <div className='digitalModule__popupContainer'>
                        <Reference />
                    </div>
                </div>
            )}

            {!submitted && (
                <>
                    <div className='digitalModule__dottedLine'></div>

                    <div className='digitalModule__test'>
                {/* Passage Section - Left Side */}
                {question.question.passages && question.question.passages.length > 0 && (
                    <>
                        <div className='digitalModule__material'>
                            <div className='digitalModule__materialContent'>
                                <div className='digitalModule__passages' id='digitalModule__passages'>
                                    {/* Figures for Reading/Writing - show before passages */}
                                    {question.subject === 'Reading and Writing' && question.question.figures && question.question.figures.length > 0 && (
                                        question.question.figures.map((figure: any) => (
                                            <img key={figure.id} src={figure.image} alt="Question figure" className='digitalModule__figure' draggable={false} />
                                        ))
                                    )}
                                    {question.question.passages.map((passage: any, index: number) => {
                                        const passageId = `passage-${index}`;
                                        return (
                                        <div 
                                            key={passageId} 
                                            className={annotate
                                                ? `digitalModule__passage digitalModule__passage--${annotationColor ? COLORS[annotationColor] : 'yellow'}`
                                                : 'digitalModule__passage'
                                            }
                                        >
                                            {passage.introduction && (
                                                <p 
                                                    className='katex digitalModule__passageText' 
                                                    dangerouslySetInnerHTML={{__html: passage.introduction}} 
                                                />
                                            )}
                                            <p 
                                                id={passageId}
                                                className='katex digitalModule__passageText' 
                                                onMouseUp={handlePassageMouseUp}
                                                onTouchEnd={handlePassageMouseUp}
                                                dangerouslySetInnerHTML={{__html: passage.passage}} 
                                            />
                                            {/* Render saved annotations */}
                                            {annotations.filter(ann => ann.passageId === passageId).map((ann, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`katex digitalModule__passageAnnotation
                                                        ${ann.color && 'digitalModule__passageAnnotation--' + COLORS[ann.color]}
                                                        ${ann.underline && 'digitalModule__passageAnnotation--' + UNDERLINE[ann.underline]}
                                                    `}
                                                    onMouseDown={() => handleAnnotationMouseDown(ann)}
                                                    onTouchStart={() => handleAnnotationMouseDown(ann)}
                                                    dangerouslySetInnerHTML={{ __html: ann.html }}
                                                />
                                            ))}
                                            {/* Render currently selected annotation */}
                                            {annotation && annotation.passageId === passageId && (
                                                <div
                                                    id='digitalModule__passageAnnotation--selected'
                                                    className={`katex digitalModule__passageAnnotation--selected
                                                        ${annotation.color && 'digitalModule__passageAnnotation--' + COLORS[annotation.color]}
                                                        ${annotation.underline && 'digitalModule__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                    `}
                                                    onMouseDown={() => handleAnnotationMouseDown(annotation)}
                                                    onTouchStart={() => handleAnnotationMouseDown(annotation)}
                                                    dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                />
                                            )}
                                        </div>
                                        );
                                    })}
                                    {/* Annotation Helper Popup */}
                                    {annotation && (
                                        <AnnotationHelper
                                            id='digitalModule__passageAnnotationHelper'
                                            annotation={annotation}
                                            annotationColor={annotationColor}
                                            annotationUnderline={annotationUnderline}
                                            notesOpen={notesOpen}
                                            setAnnotationColor={setAnnotationColor}
                                            setAnnotationUnderline={setAnnotationUnderline}
                                            setNotesOpen={setNotesOpen}
                                            setAnnotation={setAnnotation}
                                            saveAnnotation={saveAnnotation}
                                            deleteAnnotation={deleteAnnotation}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Notes Sidebar */}
                        {annotations.find(ann => ann.annotation !== null)
                            ? (notesOpen
                                ? <div id='digitalModule__notes' className='digitalModule__notesBox'>
                                    <div className='digitalModule__notesOpen' onClick={() => setNotesOpen(false)}>
                                        <MdKeyboardArrowRight />
                                    </div>
                                    <div className='digitalModule__notes'>
                                        {annotations.filter(ann => ann.annotation !== null).map((ann, idx) => (
                                            <div 
                                                key={idx}
                                                id={ann.passageId === annotation?.passageId &&
                                                    ann.startIndex === annotation?.startIndex &&
                                                    ann.endIndex === annotation?.endIndex
                                                    ? 'digitalModule__notesCard--active'
                                                    : undefined
                                                }
                                                className={(ann.passageId === annotation?.passageId &&
                                                    ann.startIndex === annotation?.startIndex &&
                                                    ann.endIndex === annotation?.endIndex)
                                                    ? 'digitalModule__notesCard--active'
                                                    : 'digitalModule__notesCard'
                                                }
                                                onClick={() => setAnnotation(ann)}
                                            >
                                                <div className={ann.color
                                                    ? `digitalModule__notesCardHeader--${COLORS[ann.color]}`
                                                    : 'digitalModule__notesCardHeader--yellow'
                                                }>
                                                    <h6 
                                                        className={ann.underline
                                                            ? `digitalModule__notesCardTitle--${UNDERLINE[ann.underline]}`
                                                            : 'digitalModule__notesCardTitle'
                                                        }
                                                        dangerouslySetInnerHTML={{ __html: ann.preview }}
                                                    />
                                                    <div className='digitalModule__notesCardDelete' onClick={(event) => removeNotes(event, ann)}>
                                                        <IoTrashOutline />
                                                    </div>
                                                </div>
                                                <textarea
                                                    placeholder='Notes are saved automatically.'
                                                    value={ann.annotation}
                                                    maxLength={1024}
                                                    onChange={(event) => {
                                                        saveAnnotation({
                                                            ...ann,
                                                            annotation: event.target.value
                                                        });
                                                        setAnnotation({
                                                            ...ann,
                                                            annotation: event.target.value
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                : <div className='digitalModule__notesBox--closed'>
                                    <div className='digitalModule__notesOpen' onClick={() => setNotesOpen(true)}>
                                        <MdKeyboardArrowLeft />
                                    </div>
                                </div>
                            )
                            : <div className='digitalModule__verticalLine'></div>
                        }
                    </>
                )}

                {/* Question Section - Right Side */}
                <div className='digitalModule__question'>
                    <div className='digitalModule__questionContent'>
                        {/* ABC Crossout Button */}
                        {question.question.choices && question.question.choices.length > 0 && (
                            <div className='digitalModule__questionActions'>
                                <button
                                    className={showCrossOutButtons 
                                        ? 'digitalModule__crossOutButton--active' 
                                        : 'digitalModule__crossOutButton'
                                    }
                                    onClick={() => setShowCrossOutButtons(!showCrossOutButtons)}
                                >
                                    Abc
                                </button>
                            </div>
                        )}
                        
                        <div className='digitalModule__questionChoices'>
                            {/* Figures for Math */}
                            {question.subject === 'Math' && question.question.figures && question.question.figures.length > 0 && (
                                question.question.figures.map((figure: any) => (
                                    <img key={figure.id} src={figure.image} alt="Question figure" className='digitalModule__figure' draggable={false} />
                                ))
                            )}

                            {/* Question Text */}
                            <p className='digitalModule__questionQuestion'>
                                <span className='katex' dangerouslySetInnerHTML={{__html: question.question.question}} />
                            </p>

                            {/* Answer Choices */}
                            {question.question.choices && question.question.choices.length > 0 && question.question.choices.map((choice: any, index: number) => {
                                const isCrossedOut = crossedOut.includes(choice.id);
                                const isSelected = answer === choice.id;
                                const isCorrect = submitted && choice.isCorrect;
                                const isWrong = submitted && isSelected && !choice.isCorrect;
                                
                                return (
                                    <div key={choice.id} className='digitalModule__questionChoiceWrapper'>
                                        <button
                                            className={
                                                isSelected 
                                                    ? 'digitalModule__questionChoice--selected' 
                                                    : isCrossedOut 
                                                    ? 'digitalModule__questionChoice--crossedOut' 
                                                    : 'digitalModule__questionChoice'
                                            }
                                            onClick={() => !submitted && handleAnswerSelect(choice.id)}
                                            disabled={submitted}
                                        >
                                            <div className={`digitalModule__questionChoiceOutline ${isCorrect ? 'digitalModule__questionChoiceOutline--correct' : ''} ${isWrong ? 'digitalModule__questionChoiceOutline--wrong' : ''}`}></div>
                                            <p className='digitalModule__questionChoiceLetter'>
                                                {String.fromCharCode(65 + index)}
                                            </p>
                                            <p className='digitalModule__questionChoiceText'>
                                                <span className='katex' dangerouslySetInnerHTML={{__html: choice.choice}} />
                                            </p>
                                        </button>
                                        {!submitted && showCrossOutButtons && (
                                            <button 
                                                className={isCrossedOut ? 'digitalModule__crossOutChoice--selected' : 'digitalModule__crossOutChoice'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCrossOut(choice.id, e);
                                                }}
                                            >
                                                {String.fromCharCode(65 + index)}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className='digitalModule__dottedLine'></div>
                </>
            )}

            {/* Show Explanation Button (if enabled before submission) */}
            {question.showExplanation && question.explanation && !submitted && (
                <button 
                    className='dailyQuestion__explanationBtn'
                    onClick={() => setExplanationOpen(!explanationOpen)}
                >
                    <MdLightbulb /> {explanationOpen ? 'Hide' : 'Show'} Explanation
                </button>
            )}
            
            {/* Explanation Popup (shows before submission if customization enabled) */}
            {question.showExplanation && explanationOpen && !submitted && (
                <div className='dailyQuestion__explanationPopup'>
                    <div className='explanationPopup__header'>
                        <h3>💡 Explanation</h3>
                        <MdClose onClick={() => setExplanationOpen(false)} />
                    </div>
                    <div className='explanationPopup__content' dangerouslySetInnerHTML={{__html: question.explanation}} />
                </div>
            )}
            
            {/* Submit Button */}
            {!submitted && (
                <div className='dailyQuestion__submitContainer'>
                    <button 
                        className='dailyQuestion__submit'
                        onClick={handleSubmit}
                        disabled={!answer}
                    >
                        Submit Answer
                    </button>
                </div>
            )}

            {/* Result Display */}
            {submitted && result && (
                <div className='dailyQuestion__result'>
                    <div className={`dailyQuestion__resultBanner ${result.isCorrect ? 'resultBanner--correct' : 'resultBanner--incorrect'}`}>
                        <span className='resultBanner__icon'>{result.isCorrect ? '✓' : '✗'}</span>
                        <span className='resultBanner__text'>
                            {result.isCorrect ? 'Correct!' : 'Incorrect'}
                        </span>
                    </div>
                    
                    {result?.correctPercentage !== null && result?.correctPercentage !== undefined && (
                        <div className='dailyQuestion__stats'>
                            <div className='stat'>
                                <span className='stat__value'>{result.correctPercentage}%</span>
                                <span className='stat__label'>got this correct</span>
                            </div>
                            <div className='stat'>
                                <span className='stat__value'>{result.totalAttempts}</span>
                                <span className='stat__label'>total attempts</span>
                            </div>
                        </div>
                    )}

                    {/* Replicate the entire test structure for results */}
                    <div className='digitalModule__test dailyQuestion__resultsTest'>
                        {/* Passage Section - Left Side */}
                        {question.question.passages && question.question.passages.length > 0 && (
                            <>
                                <div className='digitalModule__material'>
                                    <div className='digitalModule__materialContent'>
                                        <div className='digitalModule__passages'>
                                            {/* Figures for Reading/Writing - show before passages */}
                                            {question.subject === 'Reading and Writing' && question.question.figures && question.question.figures.length > 0 && (
                                                question.question.figures.map((figure: any) => (
                                                    <img key={figure.id} src={figure.image} alt="Question figure" className='digitalModule__figure' draggable={false} />
                                                ))
                                            )}
                                            {question.question.passages.map((passage: any) => (
                                                <div key={passage.id} className='digitalModule__passage'>
                                                    {passage.introduction && (
                                                        <p className='katex digitalModule__passageText' dangerouslySetInnerHTML={{__html: passage.introduction}} />
                                                    )}
                                                    <p className='katex digitalModule__passageText' dangerouslySetInnerHTML={{__html: passage.passage}} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className='digitalModule__verticalLine'></div>
                            </>
                        )}

                        {/* Question Section - Right Side */}
                        <div className='digitalModule__question'>
                            <div className='digitalModule__questionContent'>
                                <div className='digitalModule__questionChoices'>
                                    {/* Figures for Math */}
                                    {question.subject === 'Math' && question.question.figures && question.question.figures.length > 0 && (
                                        question.question.figures.map((figure: any) => (
                                            <img key={figure.id} src={figure.image} alt="Question figure" className='digitalModule__figure' draggable={false} />
                                        ))
                                    )}

                                    {/* Question Text */}
                                    <p className='digitalModule__questionQuestion'>
                                        <span className='katex' dangerouslySetInnerHTML={{__html: question.question.question}} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Show multiple choice answers */}
                    {question.question.choices && question.question.choices.length > 0 && (
                        <div className='dailyQuestion__choicesReview'>
                            {question.question.choices.map((choice: any, index: number) => {
                                const isCorrect = result?.correctChoiceId ? choice.id === result.correctChoiceId : (choice.isCorrect === true);
                                const wasSelected = answer === choice.id;
                                const isWrongAnswer = wasSelected && !isCorrect;
                                
                                return (
                                    <div 
                                        key={choice.id} 
                                        className={`dailyQuestion__reviewChoice ${
                                            isCorrect ? 'dailyQuestion__reviewChoice--correct' : 
                                            isWrongAnswer ? 'dailyQuestion__reviewChoice--wrong' : ''
                                        }`}
                                        style={{
                                            backgroundColor: isCorrect ? '#c8e6c9' : (isWrongAnswer ? '#ffcdd2' : 'white'),
                                            borderColor: isCorrect ? '#4caf50' : (isWrongAnswer ? '#f44336' : '#e0e0e0')
                                        }}
                                    >
                                        <span className='dailyQuestion__reviewChoiceLetter'>
                                            {String.fromCharCode(65 + index)}
                                        </span>
                                        <span className='katex' dangerouslySetInnerHTML={{__html: choice.choice}} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {result?.explanation && (
                        <div className='dailyQuestion__explanation'>
                            <h3>💡 Explanation</h3>
                            <div 
                                className='explanation__content katex'
                                dangerouslySetInnerHTML={{__html: result.explanation}} 
                            />
                        </div>
                    )}
                </div>
                )}
        </div>
    );
}

export default DailyQuestion;
